/**
 * Partner-attribution persistence.
 *
 * A partner link (`?ref=<key>&sub=<subid>&cid=<network_click_id>`) should keep
 * attributing the visitor for 30 days even after they navigate to a clean URL or
 * come back in a later session. Until the wallet's first on-chain purchase, URL
 * values are last-touch: a later partner link replaces the stored ref/source/
 * click-id tuple. Once the connected wallet is registered on-chain, the server
 * freezes the DB mapping and ignores later links.
 *
 * No-op inside a Telegram Mini App: there the bot's `start_param` is the
 * authoritative source and is supplied on every launch.
 */
import { deleteCookie, getCookie, refreshCookie, setCookie } from '../lib/cookies';
import { CID_COOKIE, isTelegramMiniApp, isValidNetworkClick } from './referral';

export const REF_COOKIE = 'prmd_ref';
export const SUB_COOKIE = 'prmd_sub';
export const CLICK_ID_COOKIE = 'prmd_click_id';

const SUB_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;
const CLICK_ID_RE = /^[A-Za-z0-9_-]{8,64}$/;

/**
 * The owner's referral keys (0 = organic/default plus personal keys), mirroring
 * the bot. An owner-keyed link never replaces a PARTNER attribution already
 * stored in the cookie — the owner's links are everywhere (channel posts, site
 * defaults), and without the guard they steal paid partner referrals recorded
 * first, before the wallet's first purchase locks the referrer on-chain.
 */
const OWNER_KEYS = new Set(['0', '2', '156']);

/** The stored referrer key (non-negative integer, 0 = owner) from the cookie, or null. */
export function getStoredRef(): number | null {
  const raw = getCookie(REF_COOKIE);
  if (!raw || !/^\d+$/.test(raw)) return null;
  const key = Number(raw);
  return Number.isInteger(key) && key >= 0 ? key : null;
}

/** The stored SubID from the cookie, or null. */
export function getStoredSub(): string | null {
  const raw = getCookie(SUB_COOKIE);
  return raw && SUB_ID_RE.test(raw) ? raw : null;
}

/** A stable per-browser click id, minted once and persisted (30-day cookie). */
export function getClickId(): string {
  const existing = getCookie(CLICK_ID_COOKIE);
  if (existing && CLICK_ID_RE.test(existing)) {
    setCookie(CLICK_ID_COOKIE, existing); // slide expiry
    return existing;
  }
  const id =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID().replaceAll('-', '')
      : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
  setCookie(CLICK_ID_COOKIE, id);
  return id;
}

/**
 * Persists ref/sub/network click id for this visit. New URL params overwrite the
 * stored values; absent ones keep (and refresh the 30-day expiry of) whatever is
 * stored. Runs once per page load. No-op inside Telegram.
 */
export function persistAttribution(): void {
  if (isTelegramMiniApp()) return;

  const params = new URLSearchParams(window.location.search);

  const urlRef = params.get('ref');
  const urlSub = params.get('sub');
  const urlCid = params.get('cid');
  const storedRef = getCookie(REF_COOKIE);
  const ownerStealsPartner =
    urlRef !== null &&
    OWNER_KEYS.has(urlRef) &&
    storedRef !== null &&
    /^\d+$/.test(storedRef) &&
    !OWNER_KEYS.has(storedRef);
  if (urlRef && /^(?:0|[1-9]\d*)$/.test(urlRef) && !ownerStealsPartner) {
    setCookie(REF_COOKIE, urlRef);
    setCookie(SUB_COOKIE, urlSub && SUB_ID_RE.test(urlSub) ? urlSub : 'main');
    if (urlCid && isValidNetworkClick(urlCid)) setCookie(CID_COOKIE, urlCid);
    else deleteCookie(CID_COOKIE);
  } else {
    refreshCookie(REF_COOKIE);
    refreshCookie(SUB_COOKIE);
    refreshCookie(CID_COOKIE);
  }

  // Ensure an internal browser click_id exists and slide its expiry. This is NOT
  // the partner postback click id; `prmd_cid` is used for that when present.
  getClickId();
}
